/**
 * TeamsChatPanel - Lightweight Teams chat panel
 * Connects to backend Teams API for channels and direct messages
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  AlertCircle,
  Users,
  Hash,
  Search,
  ChevronLeft,
} from 'lucide-react'
import { formatCountBadge } from '../../lib/format'
import { cn } from '../../lib/utils'
import { BACKEND_API_URL } from './types'

export function TeamsChatPanel({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const [view, setView] = useState<'channels' | 'chat' | 'users'>('channels')
  const [channels, setChannels] = useState<any[]>([])
  const [users, setUsers] = useState<any[]>([])
  const [activeChannel, setActiveChannel] = useState<any>(null)
  const [messages, setMessages] = useState<any[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const getHeaders = () => {
    const token = localStorage.getItem('IdToken') || localStorage.getItem('access_token')
    const orgId = localStorage.getItem('org_id') || localStorage.getItem('CurrentTenantId')
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(orgId ? { 'X-Organisation-ID': orgId } : {}),
    }
  }

  const apiFetch = useCallback(async (url: string, options?: RequestInit) => {
    const res = await fetch(`${BACKEND_API_URL}${url}`, {
      ...options,
      headers: { ...getHeaders(), ...options?.headers },
    })
    if (!res.ok) throw new Error(`API error: ${res.status}`)
    return res.json()
  }, [])

  useEffect(() => {
    if (isOpen) {
      setError(null)
      loadChannels()
      loadUsers()
    }
  }, [isOpen])

  const loadChannels = async () => {
    setIsLoading(true)
    try {
      const data = await apiFetch('/api/teams/channels/')
      const arr = Array.isArray(data) ? data : []
      setChannels(arr)
      if (arr.length > 0 && !activeChannel) {
        openChannel(arr[0])
      }
      setError(null)
    } catch (e: any) {
      setChannels([])
      setError('Failed to load channels')
    } finally {
      setIsLoading(false)
    }
  }

  const loadUsers = async () => {
    try {
      const data = await apiFetch('/api/teams/users/')
      setUsers(Array.isArray(data) ? data : [])
    } catch {
      setUsers([])
    }
  }

  const loadMessages = async (channelId: string) => {
    setIsLoading(true)
    try {
      const data = await apiFetch(`/api/teams/channels/${channelId}/messages/`)
      setMessages(data.messages || [])
    } catch {
      setError('Failed to load messages')
    } finally {
      setIsLoading(false)
    }
  }

  const openChannel = (channel: any) => {
    setActiveChannel(channel)
    setView('chat')
    loadMessages(channel.id)
  }

  const startDM = async (userId: string) => {
    try {
      const data = await apiFetch(`/api/teams/dm/${userId}/`)
      openChannel({
        id: data.id,
        type: 'direct',
        name: data.other_user?.name,
        other_user: data.other_user,
      })
    } catch {
      setError('Failed to start conversation')
    }
  }

  const sendMessage = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!input.trim() || !activeChannel || isLoading) return

    const content = input.trim()
    setInput('')

    const tempMsg = {
      id: `temp-${Date.now()}`,
      content,
      sender: { name: 'You' },
      is_own: true,
      created_at: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, tempMsg])

    try {
      const data = await apiFetch(`/api/teams/channels/${activeChannel.id}/messages/`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      })
      setMessages((prev) => prev.map((m) => (m.id === tempMsg.id ? { ...data, is_own: true } : m)))
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id))
      setError('Failed to send message')
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (view === 'chat' && inputRef.current) inputRef.current.focus()
  }, [view])

  const filteredChannels = useMemo(() => {
    if (!searchQuery) return channels
    const q = searchQuery.toLowerCase()
    return channels.filter((c) => c.name?.toLowerCase().includes(q))
  }, [channels, searchQuery])

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users
    const q = searchQuery.toLowerCase()
    return users.filter((u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
  }, [users, searchQuery])

  const onlineCount = useMemo(() => users.filter((u) => u.is_online).length, [users])

  if (!isOpen) return null

  return (
    <div
      className="w-96 bg-card rounded-t-xl shadow-2xl border border-border border-b-0 flex flex-col animate-in slide-in-from-bottom-2 duration-200"
      style={{ minHeight: '420px', maxHeight: '560px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-500 to-purple-600 rounded-t-xl">
        <div className="flex items-center gap-2">
          {view === 'chat' && activeChannel ? (
            <>
              <button onClick={() => { setView('channels'); setActiveChannel(null); setMessages([]) }} className="p-0.5 hover:bg-white/20 rounded">
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <span className="text-sm font-semibold text-white truncate max-w-[180px]">
                {activeChannel.type === 'direct' ? activeChannel.other_user?.name || activeChannel.name : `#${activeChannel.name}`}
              </span>
            </>
          ) : (
            <>
              <Users className="w-4 h-4 text-white" />
              <span className="text-sm font-semibold text-white">Teams</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/20 text-white">{onlineCount} online</span>
            </>
          )}
        </div>
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
          <X className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
          <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> {error}
          </p>
        </div>
      )}

      {/* Channels/Users list */}
      {(view === 'channels' || view === 'users') && !error && (
        <>
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={view === 'users' ? 'Search people...' : 'Search channels...'}
                className="w-full pl-7 pr-3 py-1.5 text-xs rounded-lg border border-border bg-card focus:ring-2 focus:ring-violet-500 outline-none"
              />
            </div>
          </div>
          <div className="flex border-b border-border">
            <button
              onClick={() => setView('channels')}
              className={cn('flex-1 py-2 text-xs font-medium', view === 'channels' ? 'text-violet-600 border-b-2 border-violet-600' : 'text-slate-500 hover:text-slate-700')}
            >
              <Hash className="w-3 h-3 inline mr-1" /> Channels
            </button>
            <button
              onClick={() => setView('users')}
              className={cn('flex-1 py-2 text-xs font-medium', view === 'users' ? 'text-violet-600 border-b-2 border-violet-600' : 'text-slate-500 hover:text-slate-700')}
            >
              <Users className="w-3 h-3 inline mr-1" /> People
            </button>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[340px] p-2 space-y-1">
            {view === 'channels' && filteredChannels.map((ch) => (
              <button
                key={ch.id}
                onClick={() => openChannel(ch)}
                className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                  <Hash className="w-4 h-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{ch.name}</p>
                  {ch.last_message && <p className="text-[10px] text-slate-400 truncate">{ch.last_message.content}</p>}
                </div>
                {ch.unread_count > 0 && (
                  <span className="min-w-5 h-5 px-1 rounded-full bg-violet-500 text-white text-[10px] font-bold leading-none flex items-center justify-center">
                    {formatCountBadge(ch.unread_count)}
                  </span>
                )}
              </button>
            ))}
            {view === 'users' && filteredUsers.map((u) => (
              <button
                key={u.id}
                onClick={() => !u.is_self && startDM(u.id)}
                disabled={u.is_self}
                className={cn(
                  'w-full flex items-center gap-2 p-2 rounded-lg transition-colors text-left',
                  u.is_self ? 'bg-violet-50 dark:bg-violet-900/20 cursor-default' : 'hover:bg-muted'
                )}
              >
                <div className="relative">
                  <div className={cn('w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold', u.is_self ? 'bg-teal-500' : 'bg-violet-500')}>
                    {u.name?.charAt(0) || '?'}
                  </div>
                  <div className={cn('absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-card', u.is_online ? 'bg-emerald-500' : 'bg-slate-300')} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {u.name}{u.is_self && <span className="text-teal-600 ml-1">(You)</span>}
                  </p>
                  <p className="text-[10px] text-slate-400 truncate">{u.email}</p>
                </div>
              </button>
            ))}
            {view === 'channels' && filteredChannels.length === 0 && (
              <div className="text-center py-8">
                <Hash className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-xs text-slate-400">No channels yet</p>
              </div>
            )}
            {view === 'users' && filteredUsers.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-xs text-slate-400">No team members found</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Chat view */}
      {view === 'chat' && activeChannel && (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[280px] max-h-[380px]">
            {isLoading ? (
              <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-violet-500" /></div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-xs text-slate-400">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className={cn('flex', msg.is_own ? 'justify-end' : 'justify-start')}>
                    <div className={cn('max-w-[85%] rounded-lg px-2.5 py-1.5', msg.is_own ? 'bg-violet-600 text-white rounded-br-sm' : 'bg-muted rounded-bl-sm')}>
                    {!msg.is_own && <p className="text-[10px] font-medium text-violet-600 dark:text-violet-400 mb-0.5">{msg.sender?.name}</p>}
                    <p className="text-xs whitespace-pre-wrap break-words">{msg.content}</p>
                    <p className={cn('text-[9px] mt-0.5', msg.is_own ? 'text-white/70' : 'text-slate-400')}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          <form onSubmit={sendMessage} className="p-2 border-t border-border">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-border bg-card focus:ring-2 focus:ring-violet-500 outline-none"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                className="px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
